// Remarketing tracking system
// ============================================
// KONFIGURACE TRACKING ID
// Nahraďte placeholder hodnoty vašimi skutečnými ID
// ============================================

// 📌 ZMĚŇTE tyto hodnoty na vaše skutečné ID:
export const TRACKING_CONFIG = {
  // Google Ads
  GOOGLE_ADS_ID: 'AW-XXXXXXXXX',           // např. AW-123456789
  GOOGLE_CONVERSION_ID: 'AW-XXXXXXXXX',    // např. AW-123456789
  GOOGLE_CONVERSION_LABEL: 'XXXXXXXXXX',   // např. AbCdEfGhIjK
  
  // Google Analytics 4
  GA4_MEASUREMENT_ID: 'G-XXXXXXXXXX',      // např. G-ABC123DEF4
  
  // Facebook
  FACEBOOK_PIXEL_ID: 'YOUR_PIXEL_ID',      // např. 1234567890123456
  
  // LinkedIn
  LINKEDIN_PARTNER_ID: 'YOUR_PARTNER_ID',  // např. 1234567
  LINKEDIN_CONVERSION_ID: 'CONVERSION_ID', // např. 12345678
  
  // Microsoft Advertising
  MICROSOFT_UET_TAG_ID: 'YOUR_UET_TAG_ID', // např. 12345678
  
  // Sklik (Seznam.cz)
  SKLIK_RETARGETING_ID: 'YOUR_SKLIK_ID',   // např. 123456
  
  // Heureka
  HEUREKA_API_KEY: 'YOUR_HEUREKA_KEY'      // váš API klíč
};

// Type declarations for tracking scripts
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    lintrk?: (...args: any[]) => void;
    uetq?: any[];
  }
  function gtag(...args: any[]): void;
  function fbq(...args: any[]): void;
}

export interface ServiceView {
  serviceId: string;
  serviceName: string;
  price: number;
  category: string;
  timestamp: number;
}

export interface UserBehavior {
  viewedServices: ServiceView[];
  addedToCart: ServiceView[];
  completedOrders: number;
  totalSpent: number;
  lastVisit: number;
  visitCount: number;
}

// Service categories for remarketing
export const REMARKETING_SERVICES = {
  'zehleni-kosil': {
    id: 'zehleni-kosil',
    name: 'Žehlení košil',
    price: 55,
    category: 'Žehlení',
    googleCategory: 'Služby > Domácnost > Žehlení'
  },
  'zehleni-halenky': {
    id: 'zehleni-halenky',
    name: 'Žehlení halenky',
    price: 55,
    category: 'Žehlení',
    googleCategory: 'Služby > Domácnost > Žehlení'
  },
  'zehleni-kalhoty': {
    id: 'zehleni-kalhoty',
    name: 'Žehlení kalhot',
    price: 55,
    category: 'Žehlení',
    googleCategory: 'Služby > Domácnost > Žehlení'
  },
  'zehleni-sukne': {
    id: 'zehleni-sukne',
    name: 'Žehlení sukně',
    price: 55,
    category: 'Žehlení',
    googleCategory: 'Služby > Domácnost > Žehlení'
  },
  'lozni-pradlo': {
    id: 'lozni-pradlo',
    name: 'Ložní prádlo',
    price: 45,
    category: 'Žehlení',
    googleCategory: 'Služby > Domácnost > Žehlení'
  },
  'opravy-odevu': {
    id: 'opravy-odevu',
    name: 'Opravy oděvů',
    price: 110,
    category: 'Opravy',
    googleCategory: 'Služby > Domácnost > Opravy oděvů'
  }
};

// User segments based on behavior
export enum UserSegment {
  NEW_VISITOR = 'new_visitor',
  SERVICE_VIEWER = 'service_viewer',
  CART_ABANDONER = 'cart_abandoner',
  FREQUENT_CUSTOMER = 'frequent_customer',
  HIGH_VALUE = 'high_value',
  INACTIVE = 'inactive'
}

class RemarketingTracker {
  private behavior: UserBehavior;

  constructor() {
    this.behavior = this.loadBehavior();
    this.updateVisitCount();
  }

  private loadBehavior(): UserBehavior {
    const stored = localStorage.getItem('user_behavior');
    if (stored) {
      return JSON.parse(stored);
    }
    return {
      viewedServices: [],
      addedToCart: [],
      completedOrders: 0,
      totalSpent: 0,
      lastVisit: Date.now(),
      visitCount: 0
    };
  }

  private saveBehavior(): void {
    localStorage.setItem('user_behavior', JSON.stringify(this.behavior));
  }

  private updateVisitCount(): void {
    this.behavior.visitCount += 1;
    this.behavior.lastVisit = Date.now();
    this.saveBehavior();
  }

  // Track service view
  trackServiceView(serviceId: string): void {
    const service = REMARKETING_SERVICES[serviceId as keyof typeof REMARKETING_SERVICES];
    if (!service) return;

    const view: ServiceView = {
      serviceId: service.id,
      serviceName: service.name,
      price: service.price,
      category: service.category,
      timestamp: Date.now()
    };

    this.behavior.viewedServices.push(view);
    this.saveBehavior();

    // Send to tracking platforms
    this.sendToGoogleAds('view_item', service);
    this.sendToFacebookPixel('ViewContent', service);
    this.sendToLinkedIn('view', service);
    this.sendToMicrosoft('view_item', service);
  }

  // Track add to cart
  trackAddToCart(serviceId: string, quantity: number = 1): void {
    const service = REMARKETING_SERVICES[serviceId as keyof typeof REMARKETING_SERVICES];
    if (!service) return;

    const cartItem: ServiceView = {
      serviceId: service.id,
      serviceName: service.name,
      price: service.price * quantity,
      category: service.category,
      timestamp: Date.now()
    };

    this.behavior.addedToCart.push(cartItem);
    this.saveBehavior();

    // Send to tracking platforms
    this.sendToGoogleAds('add_to_cart', service, quantity);
    this.sendToFacebookPixel('AddToCart', service, quantity);
    this.sendToLinkedIn('add_to_cart', service);
    this.sendToMicrosoft('add_to_cart', service, quantity);
  }

  // Track purchase
  trackPurchase(items: Array<{ serviceId: string; quantity: number; price: number }>, totalValue: number): void {
    this.behavior.completedOrders += 1;
    this.behavior.totalSpent += totalValue;
    this.saveBehavior();

    const serviceItems = items.map(item => {
      const service = REMARKETING_SERVICES[item.serviceId as keyof typeof REMARKETING_SERVICES];
      return {
        item_id: item.serviceId,
        item_name: service?.name || item.serviceId,
        price: item.price,
        quantity: item.quantity,
        item_category: service?.category || 'Služby'
      };
    });

    const transactionId = `ORDER_${Date.now()}`;

    // Google Ads Conversion
    // 📌 ZMĚŇTE: AW-CONVERSION_ID/CONVERSION_LABEL na vaše skutečné hodnoty
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      // Konverzní tracking
      window.gtag('event', 'conversion', {
        send_to: `${TRACKING_CONFIG.GOOGLE_CONVERSION_ID}/${TRACKING_CONFIG.GOOGLE_CONVERSION_LABEL}`,
        value: totalValue,
        currency: 'CZK',
        transaction_id: transactionId
      });

      // E-commerce purchase event
      window.gtag('event', 'purchase', {
        transaction_id: transactionId,
        value: totalValue,
        currency: 'CZK',
        items: serviceItems
      });
    }

    // Facebook Pixel
    if (typeof window !== 'undefined' && typeof window.fbq !== 'undefined') {
      window.fbq('track', 'Purchase', {
        value: totalValue,
        currency: 'CZK',
        content_type: 'product',
        contents: items.map(item => ({
          id: item.serviceId,
          quantity: item.quantity,
          item_price: item.price
        }))
      });
    }

    // LinkedIn
    if (typeof window !== 'undefined' && typeof window.lintrk !== 'undefined') {
      window.lintrk('track', { conversion_id: TRACKING_CONFIG.LINKEDIN_CONVERSION_ID });
    }

    // Microsoft UET
    if (typeof window !== 'undefined' && typeof window.uetq !== 'undefined') {
      window.uetq.push('event', 'purchase', {
        revenue_value: totalValue,
        currency: 'CZK'
      });
    }

    // Sklik conversion
    if (typeof window !== 'undefined' && (window as any).seznam_retargeting) {
      (window as any).seznam_retargeting.push({
        conversionId: TRACKING_CONFIG.SKLIK_RETARGETING_ID,
        value: totalValue,
        currency: 'CZK',
        orderId: transactionId
      });
    }

    // Heureka conversion
    if (typeof window !== 'undefined' && (window as any)._hrq) {
      (window as any)._hrq.push(['setOrderId', transactionId]);
      (window as any)._hrq.push(['setOrderValue', totalValue]);
      (window as any)._hrq.push(['trackConversion']);
    }
  }

  // Get user segment
  getUserSegment(): UserSegment {
    const daysSinceLastVisit = (Date.now() - this.behavior.lastVisit) / (1000 * 60 * 60 * 24);
    
    if (this.behavior.totalSpent > 5000) {
      return UserSegment.HIGH_VALUE;
    }
    
    if (this.behavior.completedOrders >= 3) {
      return UserSegment.FREQUENT_CUSTOMER;
    }
    
    if (this.behavior.addedToCart.length > 0 && this.behavior.completedOrders === 0) {
      return UserSegment.CART_ABANDONER;
    }
    
    if (this.behavior.viewedServices.length > 0) {
      return UserSegment.SERVICE_VIEWER;
    }
    
    if (daysSinceLastVisit > 30) {
      return UserSegment.INACTIVE;
    }
    
    return UserSegment.NEW_VISITOR;
  }

  // Send custom audience data
  updateCustomAudiences(): void {
    const segment = this.getUserSegment();
    
    // Google Ads Custom Audiences
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'user_segment', {
        segment: segment,
        visit_count: this.behavior.visitCount,
        total_spent: this.behavior.totalSpent
      });
    }

    // Facebook Custom Audiences
    if (typeof window !== 'undefined' && typeof window.fbq !== 'undefined') {
      window.fbq('trackCustom', 'UserSegment', {
        segment: segment,
        visit_count: this.behavior.visitCount,
        total_spent: this.behavior.totalSpent
      });
    }
  }

  private sendToGoogleAds(eventName: string, service: any, quantity: number = 1): void {
    if (typeof window === 'undefined' || typeof window.gtag === 'undefined') return;

    window.gtag('event', eventName, {
      items: [{
        id: service.id,
        name: service.name,
        category: service.category,
        price: service.price,
        quantity: quantity
      }],
      value: service.price * quantity,
      currency: 'CZK'
    });
  }

  private sendToFacebookPixel(eventName: string, service: any, quantity: number = 1): void {
    if (typeof window === 'undefined' || typeof window.fbq === 'undefined') return;

    window.fbq('track', eventName, {
      content_name: service.name,
      content_category: service.category,
      content_ids: [service.id],
      content_type: 'product',
      value: service.price * quantity,
      currency: 'CZK'
    });
  }

  private sendToLinkedIn(eventName: string, service: any): void {
    if (typeof window.lintrk === 'undefined') return;

    window.lintrk('track', { 
      conversion_id: eventName,
      service_name: service.name,
      service_category: service.category
    });
  }

  private sendToMicrosoft(eventName: string, service: any, quantity: number = 1): void {
    if (typeof window.uetq === 'undefined') return;

    window.uetq.push('event', eventName, {
      ecomm_prodid: service.id,
      ecomm_pagetype: 'product',
      ecomm_totalvalue: service.price * quantity,
      revenue_value: service.price * quantity,
      currency: 'CZK'
    });
  }

  // Get behavior data for analytics
  getBehaviorData(): UserBehavior {
    return { ...this.behavior };
  }

  // Clear behavior data (for testing)
  clearBehavior(): void {
    localStorage.removeItem('user_behavior');
    this.behavior = {
      viewedServices: [],
      addedToCart: [],
      completedOrders: 0,
      totalSpent: 0,
      lastVisit: Date.now(),
      visitCount: 0
    };
  }
}

// Global instance
export const remarketingTracker = new RemarketingTracker();
